/*
 *   File : listing.js
 *   Author : https://evoqins.com
 *   Description : SIP listing page
 *   Integrations : 
 *   Version : 1.0.0
*/

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "bootstrap";


// Components
import { CustomTab, EmptyScreen, Loader } from "../../Component/Others";
import { ActiveSIPCard, CancelledSIPCard, ExecutedSIPCard } from "../../Component/Cards";
import { ConfirmCancelSIPModal, EditSIPModal, RestartSIPModal } from "../../Component/Modal";
import { APIService } from "../../Service";
import { _scrollToTop } from "../../Helper";

// Assets
import EmptySIP from '../../Assets/Images/EmptyScreen/empty-sip.webp';

//Style
import Style from "../../Styles/Component/sip.module.scss";

// Custom data
const TAB_DATA = [
    {
        id: 1,
        label: "Active",
    },
    {
        id: 2,
        label: "Executed"
    },
    {
        id: 3,
        label: "Cancelled"
    },
];

function SIPList() {

    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const location = useLocation();
    const navigate = useNavigate();

    const [tabIndex, setTabIndex] = useState(location.state !== null ? location.state.tab_index : 1);
    const [activeSIPList, setActiveSIPList] = useState([]);
    const [executedSIPList, setExecutedSIPList] = useState([]);
    const [cancelledSIPList, setCancelledSIPList] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);

    // Modal
    const [showEditSIPModal, setShowEditSIPModal] = useState(false);
    const [showRestartSIPModal, setShowRestartSIPModal] = useState(false);
    const [showConfirmCancelSIPModal, setShowConfirmCancelSIPModal] = useState(false);
    const [selectedSIP, setSelectedSIP] = useState({});


    useEffect(() => {
        // Scroll the page to top
        _scrollToTop();
    }, []);

    useEffect(() => {
        // API calls
        if (tabIndex == 1 && activeSIPList.length === 0) {
            setDataLoading(true);
            _getActiveSIPList();
        } else if (tabIndex == 2 && executedSIPList.length === 0) {
            setDataLoading(true);
            _getExecutedSIPList();
        } else if (tabIndex === 3 && cancelledSIPList.length === 0) {
            setDataLoading(true);
            _getCancelledSIPList();
        }

    }, [tabIndex]);

    useEffect(() => {
        if (showEditSIPModal === true) {
            const edit_modal = new Modal(document.getElementById('sip-edit-modal'));
            edit_modal.show();
        }
    }, [showEditSIPModal]);

    useEffect(() => {
        if (showRestartSIPModal === true) {
            const restart_modal = new Modal(document.getElementById('sip-restart-modal'));
            restart_modal.show();
        }
    }, [showRestartSIPModal]);

    useEffect(() => {
        if (showConfirmCancelSIPModal === true) {
            const confirm_modal = new Modal(document.getElementById('sip-cancel-confirm-modal'));
            confirm_modal.show();
        }
    }, [showConfirmCancelSIPModal]);

    const _handleTabChange = (tab_id) => {

        setTabIndex(tab_id);

        // Saving current tab in location state
        navigate(location.pathname, {
            state: {
                "tab_index": tab_id
            },
            replace: true,

        });
    };


    // handle navigate to explore page
    const _handleStartInvesting = () => {
        navigate("/explore");
    };

    // handle cancel sip
    const _handleCancelSIP = () => {
        setShowConfirmCancelSIPModal(false);
        // API - update listing
        _getActiveSIPList();
        setDataLoading(false);
    };

    // handle edit sip detail
    const _handleEditSIPDetail = () => {
        setShowEditSIPModal(false);
        // API - update listing
        _getActiveSIPList();
        setDataLoading(false);
    };

    // handle confirm cancel SIP
    const _handleConfirmCancelSIP = (selected_sip) => {
        setShowConfirmCancelSIPModal(true);
        setSelectedSIP(selected_sip)
    };

    // handle confirm cancel SIP
    const _handleEditSIP = (selected_sip) => {
        setShowEditSIPModal(true);
        setSelectedSIP(selected_sip);
    };


    /////////////////////////////////
    //  API  -  Get active list data //
    const _getActiveSIPList = async () => {
        let url = "portfolio/active";
        let data = {
            "user_id": USER_DATA.unique_identifier,
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            setActiveSIPList(response.data);
        } else {
            setActiveSIPList([]);
        }

        setDataLoading(false);
    };

    //  API  -  Get active list data //
    const _getExecutedSIPList = async () => {
        let url = "portfolio/execute";
        let data = {
            "user_id": USER_DATA.unique_identifier,
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            setExecutedSIPList(response.data);
        } else {
            setExecutedSIPList([]);
        }

        setDataLoading(false);
    };

    //  API  -  Get active list data //
    const _getCancelledSIPList = async () => {
        let url = "portfolio/history";
        let data = {
            "operation_type": "SIP_STOP",
            "user_id": USER_DATA.unique_identifier,
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            setCancelledSIPList(response.data);
        } else {
            setCancelledSIPList([]);
        }

        setDataLoading(false);
    };

    return (

        <div className="margin-20px-bottom">
            <div className={`${Style.e_top_section} border-radius-16 px-4 padding-32px-top e-padding-16px-top-res overflow-hidden`}>
                <h4 className='e-font-16-res mb-0 e-primary-font-clash-display e-text-white e-font-20 e-font-weight-500 line-height-24px mb-2'>
                    Manage SIP
                </h4>
                {/* Custom tab */}
                <div className='gap-24px-res d-flex gap-40px padding-36px-top e-padding-16px-top-res'>
                    {TAB_DATA.map((tab, key) => (
                        <div key={key}>
                            <CustomTab tabIndex={tabIndex}
                                className="e-font-14-res"
                                data={tab}
                                handleClick={_handleTabChange} />
                        </div>
                    ))}
                </div>
            </div>

            <div className={`${(tabIndex === 1 && activeSIPList.length == 0) || (tabIndex === 2 && executedSIPList.length == 0) || (tabIndex === 3 && cancelledSIPList.length == 0) ? "col-12" : "col-md-10 col-xl-7"} p-3 card-box-shadow e-bg-white mt-4 min-vh-100`}>

                {
                    dataLoading ?
                        <Loader wrapperClassName="min-vh-100 margin-150px-top" />
                        :
                        <div className="col-12 col-lg-10 d-flex flex-column gap-3 min-vh-100 mb-5">
                            {
                                tabIndex == 1 && (
                                    activeSIPList.length !== 0 ?
                                        activeSIPList.map((product, index) => (
                                            <ActiveSIPCard key={index}
                                                product={product}
                                                handleEditSIP={() => _handleEditSIP(product)}
                                                handleCancelSIP={() => _handleConfirmCancelSIP(product)} />
                                        ))
                                        :
                                        <EmptyScreen
                                            type={1}
                                            emptyScreenImage={EmptySIP}
                                            description="Haven't started SIP yet? Invest smart, Invest Consistently!"
                                            btnLabel="Start Investing"
                                            onClick={_handleStartInvesting} />
                                )
                            }
                            {
                                tabIndex == 2 && (
                                    executedSIPList.length !== 0 ?
                                        executedSIPList.map((product, index) => (
                                            <ExecutedSIPCard key={index}
                                                product={product} />
                                        ))
                                        :
                                        <EmptyScreen
                                            type={1}
                                            emptyScreenImage={EmptySIP}
                                            description="Don’t Have an SIP Yet? Start small, stay consistent, and let compounding grow your wealth!"
                                            btnLabel="Start Investing"
                                            onClick={_handleStartInvesting} />
                                )
                            }
                            {
                                tabIndex == 3 && (
                                    cancelledSIPList.length !== 0 ?
                                        cancelledSIPList.map((product, index) => (
                                            <CancelledSIPCard key={index}
                                                product={product}
                                                handleRestartSIP={() => setShowRestartSIPModal(true)} />
                                        ))
                                        :
                                        <EmptyScreen
                                            type={1}
                                            emptyScreenImage={EmptySIP}
                                            description="No Cancelled SIPs? That’s a great sign! Keep investing and stay on track for wealth creation."
                                            btnLabel="Start Investing"
                                            onClick={_handleStartInvesting} />
                                )
                            }
                        </div>
                }
            </div>

            {
                showEditSIPModal &&
                <EditSIPModal userData={USER_DATA}
                    selectedSIP={selectedSIP}
                    handleClose={() => setShowEditSIPModal(false)}
                    handleSuccess={_handleEditSIPDetail} />
            }

            {
                showRestartSIPModal &&
                <RestartSIPModal handleClose={() => setShowRestartSIPModal(false)}
                    handleSuccess={() => setShowRestartSIPModal(false)} />
            }

            {
                showConfirmCancelSIPModal &&
                <ConfirmCancelSIPModal selectedSIP={selectedSIP}
                    handleClose={() => setShowConfirmCancelSIPModal(false)}
                    handleSuccess={_handleCancelSIP} />
            }

        </div>
    )
}

export default SIPList